import { cloneDeep } from 'lodash';
import cookies from 'js-cookie';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
import isMobileOrTabletBreakpoint from '@zocdoc-frontend-common/utils-deviceutils/isMobileOrTabletBreakpoint';
import logToDataDog from '../../../config/logToDataDog';

const PATIENT_GROWTH_SERVICE_HOST = process.env.AWS_API_HOST;

export async function sendPostRequestToPgs(
    bookingId,
    surveyQuestionsData,
    eventType,
    answersSelected = null
) {
    try {
        const xsrfToken = cookies.get('xsrf');
        let questionsToPost = getQuestionsToPost(
            surveyQuestionsData,
            eventType,
            answersSelected
        );

        const growthServiceEndpoint = `${PATIENT_GROWTH_SERVICE_HOST}/patient-growth/v1/requests/survey`;
        const response = await fetch(growthServiceEndpoint, {
            method: 'POST',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': xsrfToken,
            },
            body: JSON.stringify({
                survey_id: surveyQuestionsData.survey_id,
                survey_type: surveyQuestionsData.survey_type,
                survey_subtype: surveyQuestionsData.survey_subtype,
                questions: questionsToPost,
                booking_id: bookingId,
                user_agent: window.navigator.userAgent,
                client_platform: isMobileOrTabletBreakpoint()
                    ? 'MobileWeb'
                    : 'Desktop',
                tracking_id: cookies.get('firstTimeVisitor'),
                session_id: cookies.get('bsid'),
            }),
        });
        if (eventType === 'view') {
            if (response.status === 200) {
                logToDataDog({
                    name: 'booking-web-app.postToPgs.view_Success',
                    type: 'increment',
                    value: 1,
                });
            } else {
                logToDataDog({
                    name: 'booking-web-app.postToPgs.view_Failure',
                    type: 'increment',
                    tags: [`status:${response.status}`],
                    value: 1,
                });
            }
        } else {
            if (response.status === 200) {
                logToDataDog({
                    name: 'booking-web-app.postToPgs.submit_Success',
                    type: 'increment',
                    value: 1,
                });
            } else {
                logToDataDog({
                    name: 'booking-web-app.postToPgs.submit_Failure',
                    type: 'increment',
                    tags: [`status:${response.status}`],
                    value: 1,
                });
            }
        }
    } catch (e) {
        Sentry.captureException(new Error(e));
        if (eventType === 'view') {
            logToDataDog({
                name: 'booking-web-app.postToPgs.view_Failure',
                type: 'increment',
                tags: ['status:error'],
                value: 1,
            });
        } else {
            logToDataDog({
                name: 'booking-web-app.postToPgs.submit_Failure',
                type: 'increment',
                tags: ['status:error'],
                value: 1,
            });
        }
    }
}

function getQuestionsToPost(surveyQuestionsData, eventType, answersSelected) {
    const questionsToPost = cloneDeep(surveyQuestionsData.questions);
    questionsToPost[0].event_type = eventType;
    questionsToPost[0].event_timestamp_utc = moment.utc().format('YYMMDDHHmm');
    if (eventType === 'submit') {
        if (!answersSelected) {
            throw new Error('no answersSelected on submit survey event');
        }

        questionsToPost[0].answers_selected = [answersSelected];
    }
    return questionsToPost;
}

export async function fetchSurvey(bookingId) {
    try {
        const xsrfToken = cookies.get('xsrf');
        const growthServiceEndpoint = `${PATIENT_GROWTH_SERVICE_HOST}/patient-growth/v1/requests/survey?survey_type=postbooking&booking_id=${bookingId}`;
        const response = await fetch(growthServiceEndpoint, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': xsrfToken,
            },
            credentials: 'include',
        });

        if (response.status !== 200) {
            logToDataDog({
                name: 'booking-web-app.clientSideFetchQuestions_Failure',
                type: 'increment',
                value: 1,
                tags: [`statusCode:${response.status}`],
            });
        } else {
            logToDataDog({
                name: 'booking-web-app.clientSideFetchQuestions_Success',
                type: 'increment',
                value: 1,
                tags: [`statusCode:${response.status}`],
            });
            return await response.json();
        }
        return {};
    } catch (e) {
        Sentry.captureException(new Error(e));
        logToDataDog({
            name: 'booking-web-app.clientSideFetchQuestions_Failure',
            type: 'increment',
            value: 1,
            tags: [`statusCode:error`],
        });
    }
}
